// Forms — the list of templates a company has built.
//
// Parity pass 2 batch 4. Manager+ only; the fill side arrives in batch 5 and
// is open to everyone.
//
// The one thing this page has to communicate that a plain list would not: a
// PUBLISHED form with unpublished draft changes is a normal, common state, and
// the person looking at the list needs to know their edits are not live yet.
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  forms,
  type FormTemplate,
  type LinkKind,
} from '../formsApi';
import { SortHeader } from '../components/ui/SortHeader';
import { sortRows, useSort } from '../lib/sortRows';
import { ApiError } from '../api';
import {
  ActionMenu,
  CardList,
  EmptyState,
  LinkedRow,
  Modal,
  PageHeader,
  RowCard,
  StatusBadge,
} from '../components/ui';

const LINK_LABEL: Record<LinkKind, string> = {
  job: 'Against a job',
  asset: 'Against a machine',
  any: 'On its own',
};

/** The status tabs (round 28). One fetch, filtered client-side. */
const TABS = [
  { key: 'all', label: 'All' },
  { key: 'draft', label: 'Drafts' },
  { key: 'published', label: 'Published' },
  { key: 'archived', label: 'Archived' },
] as const;
type TabKey = (typeof TABS)[number]['key'];
type SortKey = 'name' | 'target' | 'size' | 'status' | 'published';

/** "3 sections · 12 questions", or the honest empty reading. */
function sizeOf(template: FormTemplate): string {
  const sections = template.content.sections.length;
  const questions = template.content.sections.reduce((n, s) => n + s.fields.length, 0);
  if (questions === 0) return 'No questions yet';
  return `${sections} section${sections === 1 ? '' : 's'} · ${questions} question${questions === 1 ? '' : 's'}`;
}

/** "v3 · 12/08/2026 · Dana", or "Never". */
function publishedLabel(template: FormTemplate): string {
  const current = template.currentVersion;
  if (!current) return 'Never';
  const date = new Date(current.publishedAt).toLocaleDateString('en-AU');
  return current.publishedByName
    ? `v${current.version} · ${date} · ${current.publishedByName}`
    : `v${current.version} · ${date}`;
}

export function TemplateListPage() {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState<FormTemplate[] | null>(null);
  const [tab, setTab] = useState<TabKey>('all');
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const { sortKey, sortDir, onSort } = useSort<SortKey>('name');

  const load = useCallback(async () => {
    try {
      const result = await forms.listTemplates('all');
      setTemplates(result.templates);
      setError(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load your forms.');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  // "All" still keeps archived forms out of the way — they have their own
  // tab, the same reading as the resources list. Filtered and sorted in one
  // place, so the phone cards and the table below can never disagree.
  const shown = useMemo(() => {
    const filtered = (templates ?? []).filter((t) =>
      tab === 'all' ? t.status !== 'archived' : t.status === tab
    );
    return sortRows(
      filtered,
      (template) => {
        switch (sortKey) {
          case 'name':
            return template.name;
          case 'target':
            return LINK_LABEL[template.linkKind];
          // The QUESTION COUNT, not the "3 sections · 12 questions" string
          // the cell shows — as text that sorts 1, 12, 3.
          case 'size':
            return template.content.sections.reduce((n, s) => n + s.fields.length, 0);
          case 'status':
            return template.status;
          case 'published':
            return template.currentVersion
              ? new Date(template.currentVersion.publishedAt)
              : null;
        }
      },
      sortDir
    );
  }, [templates, tab, sortKey, sortDir]);

  async function duplicate(template: FormTemplate) {
    try {
      const result = await forms.duplicateTemplate(template.id);
      // Straight into the copy: duplicating is the first step of editing it.
      navigate(`/app/forms/templates/${result.template.id}`);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not duplicate that form.');
    }
  }

  async function setArchived(template: FormTemplate, archived: boolean) {
    try {
      await forms.archiveTemplate(template.id, archived);
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not change that form.');
    }
  }

  const menuFor = (template: FormTemplate) => (
    <ActionMenu
      items={[
        { label: 'Duplicate', onClick: () => void duplicate(template) },
        template.status === 'archived'
          ? { label: 'Restore form', onClick: () => void setArchived(template, false) }
          : {
              label: 'Archive form',
              tone: 'warning' as const,
              onClick: () => void setArchived(template, true),
            },
        // No permanent delete, whatever ZT does: every submission points at a
        // version of a template (round 28 keeping the round-16 rule).
      ]}
    />
  );

  return (
    <>
      <PageHeader
        title="Forms"
        subtitle="Prestarts, checklists, incident reports: whatever your crew fills in."
        actions={
          <button type="button" className="btn-primary btn-sm" onClick={() => setCreating(true)}>
            New form
          </button>
        }
      />

      {error && (
        <div className="banner-error mb-4" role="alert">
          {error}
        </div>
      )}

      <div className="mb-4 flex flex-wrap items-center gap-2" role="tablist" aria-label="Status">
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            role="tab"
            aria-selected={tab === t.key}
            className={tab === t.key ? 'btn-primary btn-sm' : 'btn-secondary btn-sm'}
            onClick={() => setTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {templates && shown.length === 0 ? (
        <EmptyState
          title={tab === 'archived' ? 'Nothing archived' : 'No forms yet'}
          body={
            tab === 'archived'
              ? 'Forms you archive appear here. They are never deleted, because the submissions filled in against them are kept.'
              : 'Build a prestart, a checklist or a site report. Managers build them; anyone on your crew can fill one in.'
          }
          action={
            tab === 'archived' ? undefined : (
              <button
                type="button"
                className="btn-primary btn-sm"
                onClick={() => setCreating(true)}
              >
                Build your first form
              </button>
            )
          }
        />
      ) : templates ? (
        <>
          <CardList>
            {shown.map((template) => (
              <RowCard
                key={template.id}
                to={`/app/forms/templates/${template.id}`}
                title={template.name}
                badges={<TemplateBadges template={template} />}
                actions={menuFor(template)}
              >
                <div>
                  {LINK_LABEL[template.linkKind]} · {sizeOf(template)}
                </div>
                <div>Published: {publishedLabel(template)}</div>
                {template.description && <div>{template.description}</div>}
              </RowCard>
            ))}
          </CardList>
          {/* The desktop half of the standard card/table pair. Until 2026-08-26
              this page rendered only the CardList, which is sm:hidden — so on a
              desktop the whole list was invisible and no template could be
              opened. Found by the guides' screenshot rig. */}
          <div className="table-wrap max-sm:hidden">
            <table className="data-table">
              <thead>
                <tr>
                  <SortHeader label="Form" sortKey="name" current={sortKey} dir={sortDir} onSort={onSort} />
                  <SortHeader label="Filled in against" sortKey="target" current={sortKey} dir={sortDir} onSort={onSort} />
                  <SortHeader label="Size" sortKey="size" current={sortKey} dir={sortDir} onSort={onSort} />
                  <SortHeader label="Status" sortKey="status" current={sortKey} dir={sortDir} onSort={onSort} />
                  <SortHeader label="Published" sortKey="published" current={sortKey} dir={sortDir} onSort={onSort} />
                  <th />
                </tr>
              </thead>
              <tbody>
                {shown.map((template) => (
                  <LinkedRow key={template.id} to={`/app/forms/templates/${template.id}`}>
                    <td className="font-medium">
                      <Link to={`/app/forms/templates/${template.id}`} className="table-link">
                        {template.name}
                      </Link>
                      {template.description && (
                        <div className="table-muted font-normal">{template.description}</div>
                      )}
                    </td>
                    <td className="table-muted">{LINK_LABEL[template.linkKind]}</td>
                    <td className="table-muted">{sizeOf(template)}</td>
                    <td>
                      <div className="flex flex-wrap gap-1">
                        <TemplateBadges template={template} />
                      </div>
                    </td>
                    <td className="table-muted">{publishedLabel(template)}</td>
                    <td className="text-right">{menuFor(template)}</td>
                  </LinkedRow>
                ))}
              </tbody>
            </table>
          </div>
        </>
      ) : (
        !error && <p className="muted">Loading…</p>
      )}

      {creating && (
        <NewTemplateModal
          onClose={() => setCreating(false)}
          onCreated={(id) => navigate(`/app/forms/templates/${id}`)}
        />
      )}
    </>
  );
}

/** The status badge cluster, shared by the card and table variants so the two
    can never disagree about what a state looks like. */
function TemplateBadges({ template }: { template: FormTemplate }) {
  return (
    <>
      <StatusBadge
        kind="expiry"
        status={
          template.status === 'published' ? 'ok' : template.status === 'draft' ? 'soon' : 'none'
        }
        label={template.status === 'published' ? 'Published' : 'Draft'}
      />
      {template.status === 'archived' && <StatusBadge kind="resource" status="archived" />}
      {/* The state a plain list would hide. */}
      {template.hasUnpublishedChanges && (
        <StatusBadge kind="expiry" status="soon" label="Unpublished changes" />
      )}
    </>
  );
}

function NewTemplateModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (id: string) => void;
}) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [linkKind, setLinkKind] = useState<LinkKind>('asset');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const result = await forms.createTemplate({
        name,
        description: description.trim() === '' ? null : description,
        linkKind,
      });
      // Straight into the builder — a form with no questions is not something
      // anyone wanted to create and then look at in a list.
      onCreated(result.template.id);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not create that form.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      title="New form"
      onClose={onClose}
      footer={
        <>
          <button type="button" onClick={onClose} className="btn-ghost btn-sm">
            Cancel
          </button>
          <button type="submit" form="new-form" disabled={busy} className="btn-primary btn-sm">
            {busy ? 'Creating…' : 'Create and build it'}
          </button>
        </>
      }
    >
      {error && (
        <div className="banner-error mb-3" role="alert">
          {error}
        </div>
      )}

      <form id="new-form" onSubmit={submit} noValidate className="legacy-page">
        <label htmlFor="form-name">
          Name
          <input
            id="form-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Plant prestart"
          />
        </label>

        <label htmlFor="form-description">
          Description
          <input
            id="form-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="A daily walk-around before the machine starts work."
          />
          <span className="hint">Optional. Shown to whoever is about to fill it in.</span>
        </label>

        <label htmlFor="form-link">
          Filled in against
          <select
            id="form-link"
            value={linkKind}
            onChange={(e) => setLinkKind(e.target.value as LinkKind)}
          >
            <option value="asset">A machine, plant or a vehicle</option>
            <option value="job">A job</option>
            <option value="any">Nothing in particular</option>
          </select>
          <span className="hint">
            This decides what the person picks before they start, and whether the form can ask
            for a meter reading; only a machine has one.
          </span>
        </label>
      </form>
    </Modal>
  );
}
